// AuthPage — three-state: 'welcome' → 'signin' or 'signup'
// Per docs/design-spec.md §4.1 (Splash/Welcome) — split-screen:
//   - top 52%: purple gradient with white-on-purple TRIMIE logo
//   - bottom 48%: black surface with hero copy + Continue with Google/Apple/Email
//
// Sign-in and Sign-up forms use the light .auth-shell with header + form body.

import { useState } from 'react';
import api from './api';
import Header from './components/Header';

export default function AuthPage({ onLogin, onSignedUp }) {
  const [view, setView] = useState('welcome'); // welcome | signin | waitlist
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const goTo = (v) => {
    setView(v);
    setError(null);
    setMessage(null);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/auth/signin/', { email, password });
      const token = res.data?.data?.token;
      if (token) {
        localStorage.setItem('token', token);
        onLogin();
      } else {
        setError('Login failed — no token returned.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await api.post('/waitlist/', { email, name });
      setMessage("You're on the list. We'll email you when your spot opens up.");
      setEmail('');
      setName('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not add you to the waitlist. Try again?');
    } finally {
      setLoading(false);
    }
  };

  // ── Welcome screen ───────────────────────────────────────────────────────
  if (view === 'welcome') {
    return (
      <div className="welcome-shell">
        <div className="welcome-top">
          <span className="welcome-wordmark" aria-label="TRIMIE">TRIMIE</span>
        </div>
        <div className="welcome-bottom">
          <div className="welcome-hero">
            <h1>Virtually try on clothes before you buy.</h1>
            <p className="sub">Upload your photo, try on real outfits instantly.</p>
          </div>
          <div className="welcome-cta">
            <button className="btn-outline" onClick={() => goTo('waitlist')} disabled={loading}>
              Join the waitlist
            </button>
            <div className="welcome-login-link">
              Already invited?{' '}
              <button onClick={() => goTo('signin')} type="button">Sign in</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Sign in / Waitlist form ──────────────────────────────────────────────
  const isWaitlist = view === 'waitlist';

  return (
    <div className="auth-shell">
      <Header
        title={isWaitlist ? 'Join the waitlist' : 'Sign in'}
        onBack={() => goTo('welcome')}
      />
      <div className="auth-form-body">
        <div>
          <h2>{isWaitlist ? 'Get early access' : 'Welcome back'}</h2>
          <div className="auth-sub">
            {isWaitlist
              ? "We're letting people in in batches. Drop your email and we'll send an invite when your spot opens up."
              : 'Continue your closet across devices.'}
          </div>
        </div>

        {message && <div className="alert success">{message}</div>}
        {error && <div className="alert error">{error}</div>}

        {isWaitlist ? (
          <form onSubmit={handleJoinWaitlist} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label className="form-label">Email</label>
              <input type="email" placeholder="you@example.com" value={email}
                     onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div>
              <label className="form-label">Name (optional)</label>
              <input type="text" placeholder="First name" value={name}
                     onChange={e => setName(e.target.value)} autoComplete="given-name" />
            </div>
            <div style={{ flex: 1 }} />
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading
                ? <><span className="spinner" /> Adding you…</>
                : 'Add me to the waitlist'}
            </button>
            <button type="button" className="btn-outline" onClick={() => goTo('signin')}>
              Already invited? Sign in
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label className="form-label">Email</label>
              <input type="email" placeholder="you@example.com" value={email}
                     onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div>
              <label className="form-label">Password</label>
              <input type="password" placeholder="Your password" value={password}
                     onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            <div className="auth-forgot">Forgot password?</div>
            <div style={{ flex: 1 }} />
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading
                ? <><span className="spinner" /> Signing in...</>
                : 'Sign In'}
            </button>
            <button type="button" className="btn-outline" onClick={() => goTo('waitlist')}>
              Don't have an invite? Join waitlist
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// OAuth provider glyph components were removed when the welcome screen lost
// its Google/Apple CTAs in the waitlist rework. Bring them back when Google/Apple
// OAuth is actually wired up.
